import { createSlice } from '@reduxjs/toolkit';
import { deleteCookie, setCookie } from 'cookies-next';
import { IRedux, IUser } from '../types';
import { FEATURED_BRANDS } from '../../_utils/Constants';

const initialState: IRedux['auth'] = {
  user: null,
  tokens: {},
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: '',
  activeWindow: 'login',
};

export const { actions: authActions, reducer: authReducer } = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = '';
    },
    logout: () => {
      deleteCookie('user_id', { path: '/' });
      deleteCookie('access_token', { path: '/' });
      deleteCookie('refresh_token', { path: '/' });

      return initialState;
    },
    setUser: (
      state,
      action: {
        payload: IUser;
        type: string;
      }
    ) => {
      const user = action.payload;
      let shipperId = FEATURED_BRANDS['yaponamama'];


      // Set user for specific brand (including refresh_token for auto-refresh)
      state.tokens[shipperId] = {
        access_token: user.access_token,
        refresh_token: user.refresh_token,
        client_id: user.id,
      };

      // Keep backward compatibility
      state.user = user;

      // Set cookies only for yaponamama (first login)
      setCookie('user_id', user.id, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      });
      setCookie('access_token', user.access_token, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      });
      setCookie('refresh_token', user.refresh_token, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      });
    },
    setActiveWindow: (state, action) => {
      state.activeWindow = action.payload;
    },
    updateUser: (
      state,
      action: {
        payload: {
          name: string; date_of_birth: string | null,
          shipperId: string
        };
      }
    ) => {
      if (state.user) {
        state.user.name = action.payload.name;
        state.user.date_of_birth = action.payload.date_of_birth;
      }
    },
    setPhone: (
      state,
      action: {
        payload: { phone: string; shipperId: string }
      }
    ) => {
      if (state.user)
        state.user.phone = action.payload.phone;
    },
    updateTokens: (
      state,
      action: {
        payload: { access_token: string; refresh_token: string; client_id: string; shipperId: string };
      }
    ) => {
      const { shipperId, access_token, refresh_token, client_id } = action.payload;
      
      // Initialize token object if it doesn't exist
      if (!state.tokens[shipperId]) {
        state.tokens[shipperId] = { access_token: '', client_id: '' };
      }
      
      state.tokens[shipperId].access_token = access_token;
      state.tokens[shipperId].refresh_token = refresh_token;
      state.tokens[shipperId].client_id = client_id;

      // Set cookies only for yaponamama
      if (shipperId === FEATURED_BRANDS['yaponamama'] && state.user) {
        state.user.access_token = access_token;
        state.user.refresh_token = refresh_token;

        setCookie('access_token', access_token, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        });
        setCookie('refresh_token', refresh_token, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        });
      }
    },
    
    /**
     * Set brand-specific token (used when entering a brand page)
     * This stores the token obtained from mikaService.refresh
     */
    setBrandToken: (
      state,
      action: {
        payload: {
          shipperId: string;
          access_token: string;
          refresh_token: string;
          client_id: string;
        };
      }
    ) => {
      const { shipperId, access_token, refresh_token, client_id } = action.payload;
      
      state.tokens[shipperId] = {
        access_token,
        refresh_token,
        client_id,
      };
    },
    
    /**
     * Clear brand-specific token
     */
    clearBrandToken: (
      state,
      action: { payload: { shipperId: string } }
    ) => {
      const { shipperId } = action.payload;
      delete state.tokens[shipperId];
    },
  },
});
